import { Flex, Button } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import dayjs from "dayjs";
import styled from "styled-components";
import "dayjs/locale/tr";
import DataTable, { Column } from "../common/Table/DataTable";
import {
  deleteWorkPermit,
  getWorkPermitsForEmployee,
} from "../../api/workPermitApi";
import PlusIcon from "../common/Icons/PlusIcon";
import { theme, media } from "../../theme";
import TableDeleteButton from "../common/Buttons/TableDeleteButton";
import AreYouSureModal from "../common/Modals/AreYouSureModal";
import { deleteNotification } from "../../utils/useNotification";
dayjs.locale("tr");

/**
 * Personelin izin listesi.
 *
 * Kendi tablosunu ve 360px sabit yükseklikli kaydırma kutusunu bıraktı,
 * `DataTable`'a geçti. Sayfalama da artık `position: absolute; bottom: -10px`
 * ile kutunun dışına asılmıyor — o çözüm sabit yüksekliğe bağlıydı ve dar
 * ekranda üstteki içeriğin üzerine biniyordu.
 */
const WorkPermitsTable = () => {
  const { id }: any = useParams();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<any>();
  const [itemToDeleteId, setitemToDeleteId] = useState<any>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  //for pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<any>(
    window.devicePixelRatio === 1.25 ? "4" : "5"
  );
  const [totalPages, setTotalPages] = useState(1);

  const getTableData = async () => {
    try {
      const { data }: any = await getWorkPermitsForEmployee({
        pageNumber: page,
        limit,
        employeeId: id,
      });
      setTableData(data?.data);
      setTotalPages(data?.meta?.totalPages);
    } catch (error) {}
  };

  const handleDeleteClick = async (workPermitId: any) => {
    try {
      const { data }: any = await deleteWorkPermit({
        workPermitId,
        employeeId: id,
      });
      deleteNotification(data?.success);
      getTableData();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (page) {
      getTableData();
    }
  }, [page]);

  const columns: Column[] = [
    {
      key: "start",
      header: "İzin Başlangıç Tarihi",
      width: "20%",
      render: (row: any) => dayjs(row.start).format("DD/MM/YYYY"),
    },
    {
      key: "end",
      header: "İzin Bitiş Tarihi",
      width: "20%",
      render: (row: any) => dayjs(row.end).format("DD/MM/YYYY"),
    },
    {
      key: "type",
      header: "İzin Tipi",
      width: "20%",
      render: (row: any) => (row.type === "paid" ? "Ücretli" : "Ücretsiz"),
    },
    {
      key: "note",
      header: "İzin Notu",
      width: "30%",
      render: (row: any) => (row.note ? row.note : "Not mevcut değil."),
    },
    {
      key: "actions",
      header: "",
      width: "10%",
      headerAlign: "right",
      render: (row: any) => (
        <Flex justify="flex-end">
          <TableDeleteButton
            onClick={(e: any) => {
              e.stopPropagation();
              setitemToDeleteId(row?.id);
              setOpenDeleteModal(true);
            }}
          />
        </Flex>
      ),
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        /* start idi: DataTable tam genişlik alamıyordu. */
        alignItems: "stretch",
        gap: "15px",
      }}
    >
      <HeaderRow>
        <SectionTitle>İzin Tablosu</SectionTitle>
        <Button
          color={theme.colors.brand.normal}
          leftSection={<PlusIcon />}
          onClick={() => navigate(`/employees/${id}/work-permits/create`)}
        >
          Yeni İzin
        </Button>
      </HeaderRow>
      <DataTable
        columns={columns}
        rows={tableData ?? []}
        rowKey={(row: any) => row._id}
        emptyText="Bu personel için izin kaydı yok."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <AreYouSureModal
        opened={openDeleteModal}
        setOpened={setOpenDeleteModal}
        proccessFunc={() => handleDeleteClick(itemToDeleteId)}
        title="İzni Sil"
        text="Bu izni silmek istediğinizden emin misiniz?"
        confirmButtonText="Sil"
        cancelButtonText="Vazgeç"
      />
    </div>
  );
};

export default WorkPermitsTable;

const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    button {
      width: 100%;
    }
  }
`;

const SectionTitle = styled.div`
  font-weight: 600;
  color: black;
  font-size: 20px;
  line-height: 24px;
`;
